import type { SourcePlatform } from '~entities/import-job/api/types';
import type { ClassData } from '~pages/classes/bulk-upload/validation';
import type { ParentRegistrationData } from '~pages/parents/bulk-upload/validation';

import { isStudioProPlatform } from './header-aliases-fallback';

export function splitPersonName(full: string | undefined | null): {
  firstName: string;
  lastName: string;
} {
  const trimmed = String(full ?? '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) return { firstName: trimmed, lastName: '' };
  return {
    firstName: trimmed.slice(0, spaceIdx).trim(),
    lastName: trimmed.slice(spaceIdx + 1).trim(),
  };
}

function parseClockTimeToMinutes(value: string): number | null {
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!match) return null;
  let hours = parseInt(match[1] ?? '0', 10);
  const minutes = parseInt(match[2] ?? '0', 10);
  const period = match[4]?.toLowerCase();
  if (period === 'pm' && hours < 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function parseDurationToMinutes(value: string): number | null {
  const trimmed = value.trim();
  const parts = trimmed.split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 3) {
    return parts[0] * 60 + parts[1] + Math.round(parts[2] / 60);
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return null;
}

/** Enrollio class CSV expects `h:mm AM|PM` (see bulk-upload validation). */
function formatMinutesAsEnrollioClockTime(totalMinutes: number): string {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours24 = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

const DAY_NAME_TO_ENROLLIO: Record<string, string> = {
  sun: 'Sun',
  sunday: 'Sun',
  mon: 'Mon',
  monday: 'Mon',
  tue: 'Tue',
  tues: 'Tue',
  tuesday: 'Tue',
  wed: 'Wed',
  wednesday: 'Wed',
  thu: 'Thu',
  thur: 'Thu',
  thurs: 'Thu',
  thursday: 'Thu',
  fri: 'Fri',
  friday: 'Fri',
  sat: 'Sat',
  saturday: 'Sat',
};

/** Normalize day tokens to Enrollio `Sun`…`Sat` (comma-separated). */
export function canonicalizeDays(
  value: string | undefined | null,
): string | null {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (!parts.length) return null;
  return parts
    .map((part) => DAY_NAME_TO_ENROLLIO[part.toLowerCase()] ?? part)
    .join(', ');
}

export function computeEndTimeFromStartAndDuration(
  startTime: string | undefined | null,
  duration: string | undefined | null,
): string | null {
  const startMinutes = startTime ? parseClockTimeToMinutes(startTime) : null;
  const durationMinutes = duration ? parseDurationToMinutes(duration) : null;
  if (startMinutes == null || durationMinutes == null) return null;
  return formatMinutesAsEnrollioClockTime(startMinutes + durationMinutes);
}

export function enrichDancebizClassRows<T extends Record<string, unknown>>(
  rows: T[],
): T[] {
  return rows.map((row) => {
    let next: Record<string, unknown> = { ...row };
    if (!String(next['End Time'] ?? '').trim()) {
      const startTime = String(
        next.Time ??
          next['Start Time'] ??
          next['Start Time x'] ??
          '',
      ).trim();
      const duration = String(
        next.Duration ?? next.Length ?? next['Length x'] ?? '',
      ).trim();
      const endTime = computeEndTimeFromStartAndDuration(startTime, duration);
      if (endTime) {
        next['End Time'] = endTime;
      }
    }
    for (const key of ['Days', 'Day', 'Day x'] as const) {
      const raw = String(next[key] ?? '').trim();
      if (!raw) continue;
      const canonical = canonicalizeDays(raw);
      if (canonical) next[key] = canonical;
    }
    return next as T;
  });
}

function splitCombinedParentName(row: ParentRegistrationData): void {
  if (row['Parent Last Name']?.trim()) return;
  const { firstName, lastName } = splitPersonName(row['Parent First Name']);
  if (!lastName) return;
  row['Parent First Name'] = firstName;
  row['Parent Last Name'] = lastName;
}

function splitCombinedStudentName(
  row: ParentRegistrationData,
  slot = 1,
): void {
  const fnKey = `Student ${slot} First Name` as keyof ParentRegistrationData;
  const lnKey = `Student ${slot} Last Name` as keyof ParentRegistrationData;
  if (String(row[lnKey] ?? '').trim()) return;
  const { firstName, lastName } = splitPersonName(
    String(row[fnKey] ?? ''),
  );
  if (!lastName) return;
  row[fnKey] = firstName as ParentRegistrationData[typeof fnKey];
  row[lnKey] = lastName as ParentRegistrationData[typeof lnKey];
}

function fillJackrabbitStudentLastNames(row: ParentRegistrationData): void {
  const accountLast = row['Parent Last Name']?.trim() ?? '';
  if (!accountLast) return;
  for (let i = 1; i <= 6; i++) {
    const fnKey = `Student ${i} First Name` as keyof ParentRegistrationData;
    const lnKey = `Student ${i} Last Name` as keyof ParentRegistrationData;
    const firstName = row[fnKey];
    const lastName = row[lnKey];
    if (firstName && String(firstName).trim() && !String(lastName ?? '').trim()) {
      row[lnKey] = accountLast;
    }
  }
}

function applyStudioProStudentEmailRule(row: ParentRegistrationData): void {
  const parentEmail = String(row['Parent Email'] ?? '').trim().toLowerCase();
  const studentEmail = String(row['Student 1 Email'] ?? '').trim();
  if (!studentEmail) return;
  if (studentEmail.toLowerCase() === parentEmail) {
    row['Student 1 Email'] = undefined;
  }
}

function jackrabbitFamilyDedupeKey(row: ParentRegistrationData): string {
  const students: string[] = [];
  for (let i = 1; i <= 6; i++) {
    const fnKey = `Student ${i} First Name` as keyof ParentRegistrationData;
    const dobKey = `Student ${i} Date of Birth` as keyof ParentRegistrationData;
    students.push(
      String(row[fnKey] ?? '').trim().toLowerCase(),
      String(row[dobKey] ?? '').trim(),
    );
  }
  return [
    String(row['Parent First Name'] ?? '').trim().toLowerCase(),
    String(row['Parent Last Name'] ?? '').trim().toLowerCase(),
    ...students,
  ].join('|');
}

function dedupeFamilyRows<T extends ParentRegistrationData>(
  rows: T[],
  keyFn: (row: T) => string,
): T[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = keyFn(row);
    if (!key.replace(/\|/g, '').trim()) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function applyPlatformFamilyRowTransforms(
  rows: Array<ParentRegistrationData>,
  platform: SourcePlatform,
): Array<ParentRegistrationData> {
  let result = rows.map((row) => ({ ...row }));

  for (const row of result) {
    if (isStudioProPlatform(platform) || platform === 'jackrabbit') {
      splitCombinedParentName(row);
      splitCombinedStudentName(row, 1);
    }
    if (platform === 'studio_director') {
      splitCombinedParentName(row);
      for (let slot = 1; slot <= 5; slot++) {
        splitCombinedStudentName(row, slot);
      }
      fillJackrabbitStudentLastNames(row);
    }
    if (isStudioProPlatform(platform)) {
      applyStudioProStudentEmailRule(row);
    }
    if (platform === 'jackrabbit' || isStudioProPlatform(platform)) {
      fillJackrabbitStudentLastNames(row);
    }
  }

  if (platform === 'jackrabbit' || isStudioProPlatform(platform)) {
    result = dedupeFamilyRows(result, jackrabbitFamilyDedupeKey);
  }

  return result;
}

export function applyPlatformClassRowTransforms(
  rows: Array<ClassData>,
  platform: SourcePlatform,
): Array<ClassData> {
  if (platform === 'studio_director') {
    return enrichDancebizClassRows(rows);
  }
  if (platform !== 'jackrabbit') return rows;

  const result = enrichDancebizClassRows(rows);
  const seen = new Set<string>();
  return result.filter((row) => {
    const key = [row.Title, row.Days, row['Start Time'], row.Instructor]
      .map((v) => String(v ?? '').trim().toLowerCase())
      .join('|');
    if (!key.replace(/\|/g, '').trim()) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
